import {
  Menu as MenuBase,
  MenuItem as MenuItemBase,
  MenuTrigger,
  Popover,
  Separator as SeparatorBase,
  type MenuItemProps,
  type MenuProps,
  type MenuTriggerProps,
} from "react-aria-components";
import { FaAngleRight } from "react-icons/fa6";
import { css, cx } from "../../../../styled-system/css";
import type React from "react";
import { Button } from "./Button";
import { popoverStyles } from "../../fields/styles/styles";

interface Props<T> extends Omit<MenuProps<T>, "children">, MenuTriggerProps {
  label: React.ReactNode;
  buttonProps?: Omit<React.ComponentProps<typeof Button>, "children">;
  popoverProps?: Omit<React.ComponentProps<typeof Popover>, "children"> & { className?: string };
}

export function MenuButton<T extends object>({ label, children, buttonProps, popoverProps, ...props }: Props<T>) {
  return (
    <MenuTrigger {...props}>
      <Button {...buttonProps}>{label}</Button>
      <Popover
        offset={3}
        {...popoverProps}
        className={cx(
          popoverStyles,
          css({
            paddingY: "xsmall",
          }),
          popoverProps?.className,
        )}
      >
        {children}
      </Popover>
    </MenuTrigger>
  );
}

export const Menu = MenuBase;

export const Separator = () => (
  <SeparatorBase
    className={css({
      alignSelf: "center",
      borderTop: "1px solid {colors.lightgrey}",
      height: "1px",
      marginY: "xsmall",
      marginX: "small",
    })}
  />
);

export function MenuItem(props: MenuItemProps & { children?: React.ReactNode; className?: string }) {
  const textValue = props.textValue || (typeof props.children === "string" ? props.children : undefined);
  return (
    <MenuItemBase
      {...props}
      textValue={textValue}
      className={cx(
        css({
          cursor: "pointer",
          display: "flex",
          gap: "xsmall",
          alignItems: "center",
          border: "none",

          outline: "none",
          paddingY: "xsmall",
          paddingX: "medium",
          fontSize: "medium",
          _disabled: {
            cursor: "not-allowed",
            opacity: 0.5,
          },
          _focusVisible: {
            backgroundColor: "lightgrey",
          },
          _hover: {
            backgroundColor: "lightgrey",
          },
        }),
        props.className,
      )}
    >
      {({ hasSubmenu }) => (
        <>
          {props.children}
          {hasSubmenu && <FaAngleRight />}
        </>
      )}
    </MenuItemBase>
  );
}
